import { Target, Heart, Globe, Award } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/34825982/pexels-photo-34825982.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="University campus"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-cabernet/65"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-cream tracking-tight leading-none mb-4">
            Our Story & Mission
          </h1>
          <p className="font-body text-base sm:text-lg text-cream/80 max-w-2xl leading-relaxed">
            Founded with the belief that exposure to global markets can transform lives, 
            the Global Visionaries Scholarship is dedicated to breaking down barriers for aspiring entrepreneurs.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-20 bg-cream" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">Our Values</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              What Drives Us Forward
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, title: 'Vision', desc: 'We envision a world where every motivated student has access to global entrepreneurship opportunities.' },
              { icon: Heart, title: 'Compassion', desc: 'We believe in meeting students where they are and providing the support they need to thrive.' },
              { icon: Globe, title: 'Global Reach', desc: 'We connect students with international markets, projects, and mentors across continents.' },
              { icon: Award, title: 'Excellence', desc: 'We uphold the highest standards in our scholarship selection and mentorship programs.' },
            ].map((value) => (
              <div key={value.title} className="p-8 bg-card-surface rounded-lg border border-brass/20 shadow-sm text-center">
                <value.icon className="w-8 h-8 text-brass mx-auto mb-4" />
                <h3 className="font-heading text-xl font-medium text-cabernet mb-2">{value.title}</h3>
                <p className="font-body text-sm text-ink/70 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 sm:py-20 bg-card-surface" data-testid="about-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">Our Story</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight mb-6">
            From a Vision to a Movement
          </h2>
          <div className="space-y-5 font-body text-base text-ink/80 leading-relaxed">
            <p>
              The Global Visionaries Scholarship is a premier international immersion program 
              designed to bridge the gap between classroom theory and global commerce for ambitious 
              high school students. Its primary purpose is to remove financial barriers to professional 
              development by providing fully-funded travel, including airfare and access to exclusive 
              industry events, for students interested in the intersection of real estate and entrepreneurship.
            </p>
            <p>
              By placing students directly into diverse international markets, the scholarship aims to 
              cultivate a new generation of "Global Visionaries" who understand how the built environment 
              and innovative business practices shape communities worldwide.
            </p>
            <p>
              Through hands-on exposure to international development projects and direct mentorship from 
              global founders, recipients are empowered to expand their career aspirations beyond local 
              borders and develop the cultural intelligence necessary to lead in an increasingly 
              interconnected economy.
            </p>
          </div>
        </div>
      </section>

      {/* 501c3 Info */}
      <section className="py-16 bg-cabernet" data-testid="about-501c3">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Award className="w-10 h-10 text-brass mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-cream mb-3">
            501(c)(3) Tax-Exempt Organization
          </h2>
          <p className="font-body text-sm text-cream/70 max-w-xl mx-auto">
            All donations to the GoodLiving Scholars Scholarship are tax-deductible to the 
            extent permitted by law. Your generosity directly funds student scholarships 
            and mentorship programs.
          </p>
        </div>
      </section>
    </div>
  );
}
